import {
	useState,
	useEffect,
	useMemo,
	useCallback,
	useContext,
	useRef,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import useImageLoader from '@/common/hooks/useImage';
import { getPerson } from '@/api/query/person';
import {
	getNextMessageFeatureConversation,
	loadFeatureConversation,
	transcriptMessageFeature,
} from '@/api/query/feature';
import { CHAT_GPT_ROLES } from '../constants/chatRoles.constants';
import { CHAT_MEDIA_RETURN_TYPE, CONVERSATION_STATS_SOURCE, limitChatMessageLength } from '../constants/chatRequests.constants';
import { CHAT_ERROR_CODE } from '../constants/chatError.constants';
import { useAudioRecording } from '@/common/contexts/AudioRecordingContext';
import { MessageContext } from '@/common/contexts/message-context/MessageContext';
import { useProductContext } from '@/common/components/layout/productLayout/ProductContext';
import { useLanguage, useLanguageData } from '@/common/hooks/useLanguageData';
import { notifyConversationsChanged } from '@/common/utils/conversationEvents';
import { getVoiceRecordingFileName } from '@/common/functions/getVoiceRecordingFileName';
import { stopActiveMessageSpeech } from '../utils/messageSpeechManager';
import { getConversationMediaFromMessages } from '../utils/getConversationMediaFromMessages';
import {
	playCallConnectSound,
	playCallEndSound,
} from '../utils/voiceCallSounds';
import { getAudioPlatform } from '@/common/functions/audioPlatform';
import { bindCancellableSpeechMonitor } from '../utils/voiceSpeechDetector';
import {
	speakPersonVoice,
	stopPersonVoicePlayback,
} from '../utils/speakPersonVoice';

const VOICE_PLACEHOLDER_ID = 'voice-transcribing-placeholder-temp';
const getVoiceTimings = () => {
	const platform = getAudioPlatform();

	if (platform.isIOS) {
		return {
			listenDelayMs: 280,
			recorderPrerollMs: 380,
			trailingCaptureMs: 420,
			minBlobBytes: 64,
		};
	}

	if (platform.isMobile) {
		return {
			listenDelayMs: 300,
			recorderPrerollMs: 340,
			trailingCaptureMs: 360,
			minBlobBytes: 80,
		};
	}

	return {
		listenDelayMs: 340,
		recorderPrerollMs: 320,
		trailingCaptureMs: 300,
		minBlobBytes: 96,
	};
};

const VOICE_TRANSCRIBE_ERROR_RETRY_MS = 1500;

const normalizeChatMessage = (message) => {
	if (!message) {
		return message;
	}

	const isVideoMessage =
		message.response_format === CHAT_MEDIA_RETURN_TYPE.VIDEO || Boolean(message.video);

	if (isVideoMessage) {
		const mediaRef = message.video ?? message.image;

		return {
			...message,
			response_format: CHAT_MEDIA_RETURN_TYPE.VIDEO,
			video: mediaRef,
			image: message.image ?? mediaRef,
			created_at: message.created_at ?? new Date().toISOString(),
		};
	}

	return message;
};

const useChatLogic = (messagesEndRef, { enabled = true, onTokensExhausted } = {}) => {
	const t = useLanguageData('chat');
	const { lang } = useLanguage();
	const initialResponseRequestedRef = useRef(false);
	const voiceActiveRef = useRef(false);
	const voiceProcessingRef = useRef(false);
	const voicePhaseRef = useRef('waiting');
	const voiceTurnTimerRef = useRef(null);
	const speechMonitorCancelRef = useRef(null);
	const scheduleVoiceTurnRef = useRef(() => {});
	const personRef = useRef({});
	const [person, setPerson] = useState({});
	const { imageSrc } = useImageLoader(person?.profile_image?._id);

	const [messages, setMessages] = useState([]);
	const [message, setMessage] = useState('');
	const [showMoreInfo, setShowMoreInfo] = useState();
	const { addMessage } = useContext(MessageContext);
	const { id } = useParams();
	const activePersonIdRef = useRef(id);
	const navigate = useNavigate();
	const [isGenerating, setIsGenerating] = useState(false);
	const [isGeneratingVideo, setIsGeneratingVideo] = useState(false);
	const [isTextGenerating, setIsTextGeneration] = useState(false);

	const [isGalleryOpen, setIsGalleryOpen] = useState(false);
	const [currentImageIndex, setCurrentImageIndex] = useState(0);
	const [isVoiceCallActive, setIsVoiceCallActive] = useState(false);
	const [voiceCallSeconds, setVoiceCallSeconds] = useState(0);
	const [voiceCallPhase, setVoiceCallPhase] = useState('waiting');
	const [voiceUserSpeaking, setVoiceUserSpeaking] = useState(false);

	const { isRecording, handleStartRecording, handleStopRecording, acquireCallStream, releaseCallStream, getMonitorStream } =
		useAudioRecording();

	const { handleGetTokens } = useProductContext();

	const setVoicePhase = useCallback((phase) => {
		voicePhaseRef.current = phase;
		setVoiceCallPhase(phase);
	}, []);

	const resetVoiceCallState = useCallback(() => {
		speechMonitorCancelRef.current?.();
		speechMonitorCancelRef.current = null;
		voiceActiveRef.current = false;
		setIsVoiceCallActive(false);
		setVoiceCallSeconds(0);
		setVoiceUserSpeaking(false);
		setVoicePhase('waiting');
		window.clearTimeout(voiceTurnTimerRef.current);
		stopPersonVoicePlayback();
		voiceProcessingRef.current = false;
	}, [setVoicePhase]);

	const voiceWaveMode = useMemo(() => {
		if (voiceCallPhase === 'speaking') {
			return 'character';
		}

		if (voiceCallPhase === 'listening' && voiceUserSpeaking) {
			return 'user';
		}

		return 'silent';
	}, [voiceCallPhase, voiceUserSpeaking]);

	const endVoiceCall = useCallback(async () => {
		resetVoiceCallState();
		await handleStopRecording({ keepStream: false });
		await releaseCallStream();
		void playCallEndSound();
	}, [handleStopRecording, releaseCallStream, resetVoiceCallState]);

	const appendAssistantResponse = useCallback(async (response) => {
		if (!response) {
			return;
		}

		if (response.response_format === CHAT_MEDIA_RETURN_TYPE.IMAGE) {
			setIsGenerating(false);
			setMessages((prev) => [...prev, normalizeChatMessage(response)]);
			notifyConversationsChanged();
			return;
		}

		if (
			response.response_format === CHAT_MEDIA_RETURN_TYPE.VIDEO ||
			response.video
		) {
			setIsGeneratingVideo(false);
			setMessages((prev) => [...prev, normalizeChatMessage(response)]);
			notifyConversationsChanged();
			return;
		}

		setIsTextGeneration(false);
		setIsGenerating(false);
		setIsGeneratingVideo(false);

		if (response.message) {
			setMessages((prev) => [...prev, response.message]);
			notifyConversationsChanged();
			if (voiceActiveRef.current) {
				if (response.message.content) {
					await speakPersonVoice({
						text: response.message.content,
						personId: personRef.current._id,
						language: lang,
						onPhaseChange: setVoicePhase,
						onError: () => addMessage(t.speakError, 'error'),
					});
				} else {
					setVoicePhase('waiting');
				}
				if (voiceActiveRef.current) {
					scheduleVoiceTurnRef.current();
				}
			}
			return;
		}

		if (response.role) {
			setMessages((prev) => [...prev, response]);
			notifyConversationsChanged();
			if (voiceActiveRef.current) {
				if (response.content) {
					await speakPersonVoice({
						text: response.content,
						personId: personRef.current._id,
						language: lang,
						onPhaseChange: setVoicePhase,
						onError: () => addMessage(t.speakError, 'error'),
					});
				} else {
					setVoicePhase('waiting');
				}
				if (voiceActiveRef.current) {
					scheduleVoiceTurnRef.current();
				}
			}
		}
	}, [addMessage, lang, setVoicePhase, t.speakError]);

	const handleGetNextMessagesMutation = useMutation({
		mutationFn: (payload) => getNextMessageFeatureConversation(payload),
		onSuccess: ({ data }, variables) => {
			if (data.error_message === CHAT_ERROR_CODE.NO_LEGAL_IMAGE) {
				setIsTextGeneration(false);
				setIsGenerating(false);
				setIsGeneratingVideo(false);
				return addMessage(t.illegalContentError, 'error');
			}

			if (data.error_message === CHAT_ERROR_CODE.NO_TOKENS) {
				setIsTextGeneration(false);
				setIsGenerating(false);
				setIsGeneratingVideo(false);

				const isUserTextMessage =
					variables.return_type === CHAT_MEDIA_RETURN_TYPE.TEXT &&
					Boolean(variables.message?.trim());

				if (isUserTextMessage) {
					onTokensExhausted?.();
					return;
				}

				return addMessage(t.noTokensError, 'error');
			}

			if (data.error_message === CHAT_ERROR_CODE.IMAGE_GENERATION_FAILED) {
				setIsTextGeneration(false);
				setIsGenerating(false);
				setIsGeneratingVideo(false);
				return addMessage(t.imageGenerationError, 'error');
			}

			if (data.error_message === CHAT_ERROR_CODE.VIDEO_GENERATION_FAILED) {
				setIsTextGeneration(false);
				setIsGenerating(false);
				setIsGeneratingVideo(false);
				return addMessage(t.videoGenerationError, 'error');
			}

			if (data.error_message) {
				setIsTextGeneration(false);
				setIsGenerating(false);
				setIsGeneratingVideo(false);
				return addMessage(t.fetchMessagesError, 'error');
			}

			handleGetTokens();

			const response = Array.isArray(data) ? data[0] : data;
			void appendAssistantResponse(response);
		},
		onError: () => {
			setIsGenerating(false);
			setIsGeneratingVideo(false);
			setIsTextGeneration(false);
			setMessages((prev) => [
				...prev,
				{
					role: CHAT_GPT_ROLES.SYSTEM,
					content: t.fetchMessagesError,
					created_at: new Date().toISOString(),
				},
			]);
			if (voiceActiveRef.current) {
				setVoicePhase('waiting');
				scheduleVoiceTurnRef.current();
			}
		},
	});

	const isComposerLocked = useMemo(
		() =>
			isGenerating
			|| isGeneratingVideo
			|| isTextGenerating
			|| handleGetNextMessagesMutation.isPending,
		[
			isGenerating,
			isGeneratingVideo,
			isTextGenerating,
			handleGetNextMessagesMutation.isPending,
		],
	);

	const handleTranscribeAudioMutation = useMutation({
		mutationFn: (payload) => transcriptMessageFeature(payload),
		onSuccess: ({ data }) => {
			const transcribedText = limitChatMessageLength(
				typeof data === 'string'
					? data.trim()
					: (data?.text || data?.transcript || '').trim(),
			);

			setMessages((prev) =>
				prev.filter((msg) => msg.id !== VOICE_PLACEHOLDER_ID),
			);

			voiceProcessingRef.current = false;

			if (!transcribedText) {
				setVoiceUserSpeaking(false);
				setVoicePhase('waiting');
				if (voiceActiveRef.current) {
					scheduleVoiceTurnRef.current();
				}
				return;
			}

			setVoiceUserSpeaking(false);
			setVoicePhase('processing');

			setMessages((prev) => [
				...prev,
				{
					role: CHAT_GPT_ROLES.USER,
					content: transcribedText,
					created_at: new Date().toISOString(),
				},
			]);

			requestNextMessage({
				message: transcribedText,
				returnType: CHAT_MEDIA_RETURN_TYPE.TEXT,
			});
		},
		onError: (error) => {
			console.error('Transcription failed:', error);
			setMessages((prev) =>
				prev.filter((msg) => msg.id !== VOICE_PLACEHOLDER_ID),
			);
			setIsTextGeneration(false);
			voiceProcessingRef.current = false;
			setVoiceUserSpeaking(false);
			setVoicePhase('waiting');
			if (voiceActiveRef.current) {
				window.setTimeout(() => {
					if (voiceActiveRef.current) {
						scheduleVoiceTurnRef.current();
					}
				}, VOICE_TRANSCRIBE_ERROR_RETRY_MS);
			}
		},
	});

	const scheduleVoiceTurn = useCallback(() => {
		if (!voiceActiveRef.current) {
			return;
		}

		setVoicePhase('waiting');
		voiceProcessingRef.current = false;
		setVoiceUserSpeaking(false);
		window.clearTimeout(voiceTurnTimerRef.current);
		speechMonitorCancelRef.current?.();
		speechMonitorCancelRef.current = null;

		voiceTurnTimerRef.current = window.setTimeout(async () => {
			const voiceTimings = getVoiceTimings();

			if (
				!voiceActiveRef.current
				|| voiceProcessingRef.current
				|| voicePhaseRef.current === 'speaking'
				|| voicePhaseRef.current === 'processing'
			) {
				return;
			}

			voiceProcessingRef.current = true;
			setVoicePhase('listening');
			setVoiceUserSpeaking(false);
			const started = await handleStartRecording({ keepStream: true });
			if (!started || !voiceActiveRef.current) {
				voiceProcessingRef.current = false;
				setVoicePhase('waiting');
				return;
			}

			const monitorStream = getMonitorStream();
			const { promise: speechPromise, cancel } = bindCancellableSpeechMonitor(
				monitorStream,
				{
					onActivityChange: setVoiceUserSpeaking,
				},
			);
			speechMonitorCancelRef.current = cancel;

			await Promise.all([
				speechPromise,
				new Promise((resolve) => {
					window.setTimeout(resolve, voiceTimings.recorderPrerollMs);
				}),
			]);

			speechMonitorCancelRef.current = null;
			setVoiceUserSpeaking(false);

			if (!voiceActiveRef.current) {
				voiceProcessingRef.current = false;
				setVoicePhase('waiting');
				return;
			}

			if (!voiceActiveRef.current) {
				voiceProcessingRef.current = false;
				setVoicePhase('waiting');
				return;
			}

			await new Promise((resolve) => {
				window.setTimeout(resolve, voiceTimings.trailingCaptureMs);
			});

			const audioBlob = await handleStopRecording({ keepStream: true });
			const hasUsableAudio =
				audioBlob && audioBlob.size > voiceTimings.minBlobBytes;

			if (hasUsableAudio) {
				setVoicePhase('processing');
				const formData = new FormData();
				formData.append(
					'file',
					audioBlob,
					getVoiceRecordingFileName(audioBlob.type, 'voice_call.webm'),
				);
				formData.append('type', 'CHAT');
				handleTranscribeAudioMutation.mutate(formData);
				return;
			}

			voiceProcessingRef.current = false;
			setVoicePhase('waiting');
			scheduleVoiceTurnRef.current();
		}, getVoiceTimings().listenDelayMs);
	}, [
		handleStartRecording,
		handleStopRecording,
		handleTranscribeAudioMutation,
		getMonitorStream,
		setVoicePhase,
	]);

	scheduleVoiceTurnRef.current = scheduleVoiceTurn;

	const requestNextMessage = ({
		message: nextMessage,
		returnType = CHAT_MEDIA_RETURN_TYPE.TEXT,
	}) => {
		if (!personRef.current?._id) {
			return;
		}

		if (
			handleGetNextMessagesMutation.isPending
			|| isGenerating
			|| isGeneratingVideo
			|| isTextGenerating
		) {
			return;
		}

		setIsGenerating(false);
		setIsGeneratingVideo(false);
		setIsTextGeneration(false);

		if (returnType === CHAT_MEDIA_RETURN_TYPE.IMAGE) {
			setIsGenerating(true);
		} else if (returnType === CHAT_MEDIA_RETURN_TYPE.VIDEO) {
			setIsGeneratingVideo(true);
		} else if (returnType === CHAT_MEDIA_RETURN_TYPE.TEXT) {
			setIsTextGeneration(true);
		}

		handleGetNextMessagesMutation.mutate({
			message: nextMessage,
			person: personRef.current._id,
			return_type: returnType,
			type: 'CHAT',
			language: lang,
			stats_source: voiceActiveRef.current
				? CONVERSATION_STATS_SOURCE.CALL_MODE
				: CONVERSATION_STATS_SOURCE.CHAT,
		});
	};

	const handleLoadConversationMutation = useMutation({
		mutationFn: (payload) => loadFeatureConversation(payload),
		onSuccess: ({ data }, variables) => {
			if (String(variables.person) !== String(activePersonIdRef.current)) {
				return;
			}

			const loadedMessages = data?.messages ?? [];
			setMessages(() => loadedMessages.map(normalizeChatMessage));

			const shouldRequestResponse =
				loadedMessages.length === 0 ||
				loadedMessages[loadedMessages.length - 1]?.role === CHAT_GPT_ROLES.USER;

			if (shouldRequestResponse && !initialResponseRequestedRef.current) {
				initialResponseRequestedRef.current = true;
				requestNextMessage({
					returnType: CHAT_MEDIA_RETURN_TYPE.TEXT,
				});
			}
		},
	});

	const handleLoadPersonMutation = useMutation({
		mutationFn: ({ personId, language }) => getPerson(personId, { language }),
		onSuccess: ({ data }) => {
			personRef.current = data;
			setPerson(() => data);
		},
	});

	const handleSendMessage = useCallback(() => {
		if (voiceActiveRef.current || isComposerLocked) {
			return;
		}

		const trimmedMessage = limitChatMessageLength(message.trim());
		if (!trimmedMessage) return;

		setMessages((prev) => [
			...prev,
			{
				role: CHAT_GPT_ROLES.USER,
				content: trimmedMessage,
				created_at: new Date().toISOString(),
			},
		]);

		setMessage('');

		requestNextMessage({
			message: trimmedMessage,
			returnType: CHAT_MEDIA_RETURN_TYPE.TEXT,
		});
	}, [message, isComposerLocked]);

	const handleKeyPress = useCallback(
		(e) => {
			if (isComposerLocked) {
				return;
			}

			if (e.key === 'Enter' && !e.shiftKey) {
				e.preventDefault();
				if (message.trim()) {
					handleSendMessage();
				}
			}
		},
		[message, handleSendMessage, isComposerLocked],
	);

	const appendUserMessage = (content) => {
		const limitedContent = limitChatMessageLength(content);
		setMessages((prev) => [
			...prev,
			{
				role: CHAT_GPT_ROLES.USER,
				content: limitedContent,
				created_at: new Date().toISOString(),
			},
		]);
		setMessage('');
	};

	const handlePhotoRequest = () => {
		if (!personRef.current?._id || voiceActiveRef.current || isComposerLocked) {
			return;
		}

		const requestMessage = t.sendMePhoto;
		appendUserMessage(requestMessage);

		requestNextMessage({
			message: requestMessage,
			returnType: CHAT_MEDIA_RETURN_TYPE.IMAGE,
		});
	};

	const handleVideoRequest = () => {
		if (!personRef.current?._id || voiceActiveRef.current || isComposerLocked) {
			return;
		}

		const requestMessage = t.sendMeVideo;
		appendUserMessage(requestMessage);

		requestNextMessage({
			message: requestMessage,
			returnType: CHAT_MEDIA_RETURN_TYPE.VIDEO,
		});
	};

	const handleCancelRecording = useCallback(async () => {
		if (isRecording) {
			await handleStopRecording();
		}
	}, [isRecording, handleStopRecording]);

	const startVoiceCall = useCallback(async () => {
		if (!personRef.current?._id || voiceActiveRef.current) {
			return;
		}

		const micReady = await acquireCallStream();
		if (!micReady) {
			return;
		}

		void playCallConnectSound();

		voiceActiveRef.current = true;
		setIsVoiceCallActive(true);
		setVoiceCallSeconds(0);
		setVoicePhase('processing');
		stopActiveMessageSpeech();

		const opener = t.callMessage;
		setMessages((prev) => [
			...prev,
			{
				role: CHAT_GPT_ROLES.USER,
				content: opener,
				created_at: new Date().toISOString(),
			},
		]);
		requestNextMessage({
			message: opener,
			returnType: CHAT_MEDIA_RETURN_TYPE.TEXT,
		});
	}, [t.callMessage, requestNextMessage, setVoicePhase, acquireCallStream]);

	const handleCall = () => {
		startVoiceCall();
	};

	const handleActionClick = useCallback(async () => {
		if (voiceActiveRef.current || isComposerLocked) {
			return;
		}

		const isTextMode = message.trim().length > 0;

		if (isTextMode) {
			handleSendMessage();
		} else {
			if (isRecording) {
				const audioBlob = await handleStopRecording();
				if (audioBlob) {
					const formData = new FormData();
					formData.append(
						'file',
						audioBlob,
						getVoiceRecordingFileName(audioBlob.type, 'voice_message.webm'),
					);
					formData.append('type', 'CHAT');

					handleTranscribeAudioMutation.mutate(formData);
				}
			} else {
				await handleStartRecording();
			}
		}
	}, [
		message,
		isRecording,
		handleSendMessage,
		handleStartRecording,
		handleStopRecording,
		handleTranscribeAudioMutation,
		isComposerLocked,
	]);

	const conversationImages = useMemo(
		() => getConversationMediaFromMessages(messages),
		[messages],
	);

	const openGallery = useCallback(
		(index = 0) => {
			if (conversationImages.length > 0) {
				setCurrentImageIndex(index);
				setIsGalleryOpen(true);
			}
		},
		[conversationImages.length],
	);

	const closeGallery = useCallback(() => {
		setIsGalleryOpen(false);
		setCurrentImageIndex(0);
	}, []);

	const handlePrev = useCallback(() => {
		setCurrentImageIndex((prev) => Math.max(0, prev - 1));
	}, []);

	const handleNext = useCallback(() => {
		setCurrentImageIndex((prev) =>
			Math.min(conversationImages.length - 1, prev + 1),
		);
	}, [conversationImages.length]);

	const isSendMode = useMemo(() => message.trim().length > 0, [message]);

	const isTranscribing = handleTranscribeAudioMutation.isLoading;

	useEffect(() => {
		if (!enabled || id) {
			return;
		}

		resetVoiceCallState();
		void releaseCallStream();
		setMessages([]);
		personRef.current = {};
		setPerson({});
		initialResponseRequestedRef.current = false;
	}, [enabled, id, releaseCallStream, resetVoiceCallState]);

	useEffect(() => {
		if (!enabled || !id) {
			return;
		}

		activePersonIdRef.current = id;
		initialResponseRequestedRef.current = false;
		resetVoiceCallState();
		void releaseCallStream();
		setMessages([]);
		setIsGenerating(false);
		setIsGeneratingVideo(false);
		setIsTextGeneration(false);
		personRef.current = { _id: id };
		setPerson({});

		handleLoadPersonMutation.mutate({ personId: id, language: lang });
		handleLoadConversationMutation.mutate({
			person: id,
			type: 'CHAT',
			language: lang,
		});
	}, [id, enabled, lang]);

	useEffect(() => {
		if (!isVoiceCallActive) {
			return undefined;
		}

		const timer = window.setInterval(() => {
			setVoiceCallSeconds((prev) => prev + 1);
		}, 1000);

		return () => window.clearInterval(timer);
	}, [isVoiceCallActive]);

	useEffect(
		() => () => {
			window.clearTimeout(voiceTurnTimerRef.current);
			speechMonitorCancelRef.current?.();
			speechMonitorCancelRef.current = null;
			voiceActiveRef.current = false;
			stopPersonVoicePlayback();
			void releaseCallStream();
		},
		[releaseCallStream],
	);

	useEffect(() => {
		setTimeout(() => {
			messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
		}, 1000);
	}, [
		messages.length,
		isGenerating,
		isGeneratingVideo,
	]);

	return {
		person,
		imageSrc,
		messages,
		message,
		isSendMode,
		isGenerating,
		isGeneratingVideo,
		isTextGenerating,
		isTranscribing,
		isComposerLocked,
		isLoadingPerson: handleLoadPersonMutation.isPending,
		isLoadingConversation: handleLoadConversationMutation.isPending,
		handleLoadPersonMutation,
		handleLoadConversationMutation,
		handleGetNextMessagesMutation,

		conversationImages,
		isGalleryOpen,
		currentImageIndex,
		openGallery,
		closeGallery,
		handlePrev,
		handleNext,

		handleSendMessage,
		setMessage,
		handleKeyPress,
		handleActionClick,
		navigate,
		setShowMoreInfo,
		handlePhotoRequest,
		handleVideoRequest,

		isRecording,
		handleCancelRecording,
		handleCall,
		endVoiceCall,
		isVoiceCallActive,
		voiceCallSeconds,
		voiceCallPhase,
		voiceWaveMode,
		id,
	};
};

export default useChatLogic;
